import { useMediaQuery } from '@mantine/hooks'
import { Link, useNavigate } from "react-router-dom";
import { Button, Container, Grid, Loader, Center } from "@mantine/core";
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated'
import useAuthUser from 'react-auth-kit/hooks/useAuthUser'
import { useMeQuery, useUserTeamsQuery } from "../generated/graphql";
import UserProfileCard from "../components/UserProfileCard";
import UserMatchFeed from "components/profile/UserMatchFeed";
import UserNewFeed from "components/profile/UserNewFeed";
import { UserAuthData } from "../Types/UserDataTypes";

/**
 * Home page
 *
 * Shows an authenticated view with the user's profile card and feeds, or a
 * marketing/CTA view for unauthenticated users.
 */
export default function Home() {
    const isAuthenticated = useIsAuthenticated();
    return <>
        {isAuthenticated ? <AuthenticatedView/> : <UnauthenticatedView/>}
    </>
}

/**
 * AuthenticatedView
 *
 * Renders the logged-in user's profile card spanning the top of the page, with
 * match and news feeds below in two columns. Fetches real user data from the API.
 */
function AuthenticatedView(){
    const { data, loading } = useMeQuery();
    const authUser = useAuthUser<UserAuthData>();

    // Get the user's ID from auth context for fetching their teams
    const userId = authUser?.id ?? data?.me?.userData?.id;

    // Fetch teams that this user belongs to
    // TODO: This query filters teams by player membership, which is inefficient.
    // Consider adding a User.teams resolver on the backend for better performance.
    const { data: teamsData, loading: teamsLoading } = useUserTeamsQuery({
        variables: { userId: userId! },
        skip: !userId,
    });

    if (loading) {
        return (
            <Center style={{ minHeight: '50vh' }}>
                <Loader size="lg" />
            </Center>
        );
    }

    const avatarUrl = data?.me?.userData?.avatarUrl ?? undefined;
    const displayName = data?.me?.userData?.name ?? authUser?.name ?? 'Player';
    const roles = data?.me?.userData?.roles ?? authUser?.roles ?? [];

    // Get team info from the user's teams
    const userTeams = teamsData?.teams?.items ?? [];
    const primaryTeam = userTeams[0];
    const teamName = primaryTeam?.name;
    const franchiseName = primaryTeam?.franchise?.name;

    return (
        <>
            <Container size="lg" p="md">
                {/* Top: Profile card spans full width */}
                <Grid gutter="md">
                    <Grid.Col span={12}>
                        <UserProfileCard
                            avatarUrl={avatarUrl}
                            name={displayName}
                            title="Player"
                            team={teamName}
                            franchise={franchiseName}
                            roles={roles as string[]}
                        />
                    </Grid.Col>

                    {/* Bottom: Two columns - matches (left), news (right) */}
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        {/* TODO: Wire up player match history when we have a way to get
                            matches for a specific player. For now, showing empty state. */}
                        <UserMatchFeed
                            moreDetailsTo="/me/matches"
                            items={[]}
                            emptyMessage={teamsLoading ? "Loading..." : "No upcoming matches."}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        {/* TODO: News feed - no news data source exists yet.
                            When a news/announcements feature is implemented, wire this up.
                            For now, showing empty state. */}
                        <UserNewFeed
                            moreDetailsTo="/me/news"
                            items={[]}
                        />
                    </Grid.Col>
                </Grid>
            </Container>
        </>
    )
}

function UnauthenticatedView(){
    const mobile = useMediaQuery('only screen and (min-width: 640px)')
    const navigate = useNavigate();
    return (
        <>
            <div id="banner-slider-1" className="banner-slider">
                <div className="slider-items">
                    <div className="slider-item slider-item-1">
                        <div className="slider-item-wrap">
                            <div className="flex flex-row">
                                <div className="post-preview huge gaming-news">
                                    <div className="">
                                        <Link to="/faq" className="post-preview-title">
                                            What even is CSC?
                                        </Link>
                                        <p className="post-preview-text">
                                            Here at Counter-Strike Confederation (CSC), we are
                                            essentially a sports organization that runs different
                                            tiers of play and a draft for players. It is all free and
                                            laid back, so you can enjoy competitive Counter-Strike, casually.
                                        </p>

                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            flexDirection: 'column'
                                        }}>
                                            <Button
                                                component="a"
                                                onClick={() => navigate('/signup')}
                                                target="none"
                                                mt={16}
                                                size={mobile ? 'lg' : 'sm'}
                                                radius={'xl'}
                                                style={{fontFamily: 'Exo, sans-serif'}}
                                            >
                                                Signup
                                            </Button>

                                            <Button
                                                component="a"
                                                href="https://discord.com/invite/csc"
                                                target="none"
                                                mt={16}
                                                size={mobile ? 'lg' : 'sm'}
                                                radius={'xl'}
                                                // LeftIcon ={<ExternalLink/>}
                                                style={{fontFamily: 'Exo, sans-serif'}}
                                            >
                                                Join our discord to learn more!
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
